// Custom hook for sound/audio management
import { useState, useEffect, useRef, useCallback } from 'react';

export const useSound = () => {
    const [isSoundEnabled, setIsSoundEnabled] = useState(false);
    const audioRef = useRef(null);

    // Load preference from localStorage
    useEffect(() => {
        const storedSoundPreference = localStorage.getItem('soundPreference');
        if (storedSoundPreference !== null) {
            setIsSoundEnabled(JSON.parse(storedSoundPreference));
        }
    }, []);

    // Play/pause based on sound state
    useEffect(() => {
        if (audioRef.current) {
            if (isSoundEnabled) {
                audioRef.current.play().catch(e => console.warn("Audio play failed:", e));
            } else {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        }
        localStorage.setItem('soundPreference', JSON.stringify(isSoundEnabled));
    }, [isSoundEnabled]);

    // Handle visibility change (pause when tab hidden)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!audioRef.current) return;
            if (document.hidden) {
                if (isSoundEnabled && !audioRef.current.paused) {
                    audioRef.current.pause();
                }
            } else {
                if (isSoundEnabled) {
                    audioRef.current.play().catch(e => console.warn("Audio play on visibility resume failed:", e));
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isSoundEnabled]);

    const toggleSound = useCallback(() => {
        setIsSoundEnabled(prev => !prev);
    }, []);

    return {
        isSoundEnabled,
        setIsSoundEnabled,
        toggleSound,
        audioRef
    };
};
